/**
 * CashRewardOverlay — global "you got paid" beat, usable from anywhere
 * (connect-account bonus, daily bonus, gifts, IAP, ...). A player card drops
 * down from the top (avatar + username + balance), cash notes fly up onto it
 * (reusing the real in-game flyout when a `runFlyout` is supplied, CSS bills
 * otherwise), the balance counts up, then the card slides away and resolves.
 *
 * Self-contained DOM/CSS, no 3D. Body-level singleton.
 */

import gsap from 'gsap';

const STYLE_ID = 'sdl-cashreward-styles';

export interface CashRewardOptions {
  username: string;
  /** Avatar URL; falls back to a monogram tile when empty. */
  picture: string;
  /** Balance before the reward (the count-up start). */
  startStacks: number;
  /** Amount awarded (counts up from start to start + amount). */
  amount: number;
  /** Optional bill image for the flying notes; CSS bill used when absent. */
  noteUrl?: string | null;
  /**
   * When provided, runs the real in-game cash flyout against this card's avatar
   * (passed as the target screen point). The component calls back to start the
   * balance count-up when the last note begins its flight. Falls back to the
   * self-contained CSS bill flyout when absent.
   */
  runFlyout?: (
    targetScreenPos: { x: number; y: number },
    onLastNoteFlightStart: () => void,
  ) => Promise<void>;
  /** Override the host z-index for this beat (e.g. to sit above the offer popup).
   *  Omit to use the CSS default. */
  zIndex?: number;
}

export class CashRewardOverlay {
  public element: HTMLElement;
  private cardEl: HTMLElement;
  private avatarEl: HTMLElement;
  private nameEl: HTMLElement;
  private balanceEl: HTMLElement;
  private notesEl: HTMLElement;
  private countTween: gsap.core.Tween | null = null;

  constructor() {
    CashRewardOverlay.injectStyles();
    this.element = document.createElement('div');
    this.element.className = 'sdl-cashreward-host';
    this.element.innerHTML = `
      <div class="sdl-cashreward-card">
        <div class="sdl-cashreward-avatar"></div>
        <div class="sdl-cashreward-info">
          <div class="sdl-cashreward-name"></div>
          <div class="sdl-cashreward-balance">
            <span class="sdl-cashreward-coin">💵</span><span class="sdl-cashreward-amount"></span>
          </div>
        </div>
      </div>
      <div class="sdl-cashreward-notes" aria-hidden="true"></div>
    `;
    document.body.appendChild(this.element);
    this.cardEl = this.element.querySelector('.sdl-cashreward-card') as HTMLElement;
    this.avatarEl = this.element.querySelector('.sdl-cashreward-avatar') as HTMLElement;
    this.nameEl = this.element.querySelector('.sdl-cashreward-name') as HTMLElement;
    this.balanceEl = this.element.querySelector('.sdl-cashreward-amount') as HTMLElement;
    this.notesEl = this.element.querySelector('.sdl-cashreward-notes') as HTMLElement;
  }

  /** Run the full beat. Resolves after the card has dismissed. */
  async show(opts: CashRewardOptions): Promise<void> {
    this.element.style.zIndex = opts.zIndex != null ? String(opts.zIndex) : '';
    this.nameEl.textContent = opts.username;
    this.avatarEl.innerHTML = opts.picture
      ? `<img src="${opts.picture}" alt="" />`
      : `<span>${(opts.username[0] ?? '?').toUpperCase()}</span>`;
    this.balanceEl.textContent = CashRewardOverlay.fmt(opts.startStacks);

    // Drop the card in.
    this.element.classList.add('visible');
    this.cardEl.classList.remove('out');
    this.cardEl.classList.add('in');
    await this.delay(440);

    const start = opts.startStacks;
    const end = opts.startStacks + opts.amount;
    if (opts.runFlyout) {
      // Use the real in-game cash flyout, targeting this card's avatar centre;
      // count up the balance when the last note begins its flight.
      const r = this.avatarEl.getBoundingClientRect();
      const target = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
      await opts.runFlyout(target, () => this.countUp(start, end, 1200));
      await this.delay(300);
    } else {
      // Fallback: self-contained CSS bill flyout.
      this.flyNotes(opts.noteUrl ?? null);
      this.countUp(start, end, 1500);
      await this.delay(1600);
    }

    // Hold, then dismiss.
    await this.delay(600);
    this.cardEl.classList.remove('in');
    this.cardEl.classList.add('out');
    await this.delay(420);
    this.element.classList.remove('visible');
    this.notesEl.innerHTML = '';
  }

  private delay(ms: number): Promise<void> {
    return new Promise((r) => window.setTimeout(r, ms));
  }

  destroy(): void {
    this.countTween?.kill();
    this.element.remove();
  }

  private countUp(from: number, to: number, durationMs: number): void {
    this.countTween?.kill();
    const proxy = { v: from };
    this.countTween = gsap.to(proxy, {
      v: to,
      duration: durationMs / 1000,
      ease: 'power2.out', // matches the old 1-(1-t)^3 cubic ease-out
      onUpdate: () => { this.balanceEl.textContent = CashRewardOverlay.fmt(proxy.v); },
      onComplete: () => { this.balanceEl.textContent = CashRewardOverlay.fmt(to); },
    });
  }

  private flyNotes(noteUrl: string | null): void {
    const balRect = this.balanceEl.getBoundingClientRect();
    const targetX = balRect.left + balRect.width / 2;
    const targetY = balRect.top + balRect.height / 2;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let html = '';
    const N = 12;
    for (let i = 0; i < N; i++) {
      // Start near the bottom, spread across the width.
      const startX = vw * (0.2 + Math.random() * 0.6);
      const startY = vh * (0.72 + Math.random() * 0.22);
      const dx = targetX - startX;
      const dy = targetY - startY;
      const rot = Math.random() * 60 - 30;
      const delay = (i * 0.06 + Math.random() * 0.03).toFixed(3);
      const inner = noteUrl ? `<img src="${noteUrl}" alt="" />` : '';
      html += `<div class="sdl-cashreward-note${noteUrl ? '' : ' bill'}" style="`
        + `left:${startX.toFixed(0)}px;top:${startY.toFixed(0)}px;`
        + `--dx:${dx.toFixed(0)}px;--dy:${dy.toFixed(0)}px;--rot:${rot.toFixed(0)}deg;`
        + `animation-delay:${delay}s">${inner}</div>`;
    }
    this.notesEl.innerHTML = html;
  }

  private static fmt(n: number): string {
    const v = Math.round(n);
    if (v >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(v % 1_000_000_000 ? 1 : 0)}B`;
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(v % 1_000_000 ? 1 : 0)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(v % 1_000 ? 1 : 0)}K`;
    return `${v}`;
  }

  private static injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .sdl-cashreward-host {
        position: fixed; inset: 0;
        /* Below the PlayerProfilesOverlay flyout (.profiles-overlay z-index:45)
         * so the cash notes land ON TOP of the card, but above the 3D scene
         * (#game-container is z-index:auto). */
        z-index: 44;
        display: none;
        pointer-events: none;
      }
      .sdl-cashreward-host.visible { display: block; }

      .sdl-cashreward-card {
        position: absolute; top: 72px; left: 50%;
        transform: translate(-50%, -180px);
        opacity: 0;
        display: flex; align-items: center; gap: 12px;
        width: min(80vw, 300px);
        padding: 12px 16px;
        background: rgba(14,14,20,0.96);
        border: 1px solid rgba(0,229,255,0.3);
        border-radius: 18px;
        box-shadow: 0 16px 44px rgba(0,0,0,0.6);
      }
      .sdl-cashreward-card.in {
        animation: sdl-cashreward-drop 0.5s cubic-bezier(0.34, 1.4, 0.6, 1) forwards;
      }
      .sdl-cashreward-card.out {
        animation: sdl-cashreward-lift 0.4s ease-in forwards;
      }
      @keyframes sdl-cashreward-drop {
        0% { transform: translate(-50%, -180px); opacity: 0; }
        100% { transform: translate(-50%, 0); opacity: 1; }
      }
      @keyframes sdl-cashreward-lift {
        0% { transform: translate(-50%, 0); opacity: 1; }
        100% { transform: translate(-50%, -180px); opacity: 0; }
      }

      .sdl-cashreward-avatar {
        width: 50px; height: 50px; flex: 0 0 50px;
        border-radius: 50%; overflow: hidden;
        border: 2px solid rgba(255,215,94,0.8);
        display: flex; align-items: center; justify-content: center;
        background: linear-gradient(135deg, #2a2a3a, #14141c);
      }
      .sdl-cashreward-avatar img { width: 100%; height: 100%; object-fit: cover; }
      .sdl-cashreward-avatar span {
        font-family: 'Bebas Neue', sans-serif; font-size: 22px; color: #fff;
      }
      .sdl-cashreward-info { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
      .sdl-cashreward-name {
        font-family: 'Bebas Neue', sans-serif; font-size: 18px; letter-spacing: 1px;
        color: #fff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }
      .sdl-cashreward-balance {
        display: flex; align-items: center; gap: 6px;
        font-family: 'Rajdhani', sans-serif; font-size: 20px; font-weight: 700;
        color: #00FF88; text-shadow: 0 0 10px rgba(0,255,136,0.35);
      }
      .sdl-cashreward-coin { font-size: 16px; }

      .sdl-cashreward-notes { position: absolute; inset: 0; }
      .sdl-cashreward-note {
        position: absolute; width: 46px; height: 26px;
        opacity: 0;
        animation: sdl-cashreward-fly 0.85s cubic-bezier(0.4, 0.1, 0.3, 1) forwards;
      }
      .sdl-cashreward-note img { width: 100%; height: 100%; object-fit: contain; }
      .sdl-cashreward-note.bill {
        border-radius: 4px;
        background: linear-gradient(135deg, #1b8a4a, #0e5f31);
        border: 1px solid rgba(255,255,255,0.25);
        box-shadow: 0 4px 10px rgba(0,0,0,0.4);
      }
      .sdl-cashreward-note.bill::after {
        content: '$'; position: absolute; inset: 0;
        display: flex; align-items: center; justify-content: center;
        color: rgba(255,255,255,0.85); font-family: 'Bebas Neue', sans-serif; font-size: 16px;
      }
      @keyframes sdl-cashreward-fly {
        0% { opacity: 0; transform: translate(0, 0) rotate(0deg) scale(0.7); }
        15% { opacity: 1; }
        80% { opacity: 1; }
        100% { opacity: 0; transform: translate(var(--dx), var(--dy)) rotate(var(--rot)) scale(0.5); }
      }
    `;
    document.head.appendChild(style);
  }
}
